import React, { useState, useEffect, useRef } from 'react';
import {
  ActivityIndicator,
  ImageBackground,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextStyle,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useRoute, RouteProp } from '@react-navigation/native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { PROGRAMS_SURFACE_ENABLED } from '../config/flags';
import { ApiError } from '../types/auth';
import { RootStackParamList } from '../navigation/types';
import { useAuthScreenRedirect } from '../navigation/authRoutes';
import { useI18n } from '../i18n';
import { analytics } from '../services/analytics';
import { isSafariOnWeb } from '../utils/browserDetection';
import { WebForm } from '../utils/WebForm';
import { SocialAuthButtons } from '../components/auth/SocialAuthButtons';
import { authErrorKey } from './auth/authErrorCodes';
import { passkeyErrorKey } from './auth/passkeyErrorMessage';
import { narrowFormTitleSize } from './auth/narrowTitleSize';
import { buildRoomReturnRoutes, handedOffRoomRouteKeys } from './live-room/liveRoomReturnTo';
import { markRoomHandoff } from '../navigation/roomHandoff';
import { LegalConsentNotice } from './auth/LegalConsent';
import { api } from '../services/api';
import { AuthErrorCode } from '../components/auth/AuthErrorCode';
import { resolveAuthErrorCode, copyAuthErrorCode } from '../utils/authErrorCode';
import { forgetLiveReturn, rememberLiveReturn } from '../services/liveReturnContext';
import { resetAfterPreload } from '../navigation/resetAfterPreload';
// CORE-668 — the login page is the same visual system as the CORE-574
// register page: same background assets, palette, type scale, field frames
// and two-column layout grid. Everything visual is imported from there.
import {
  BrandLogo,
  REGISTER_BG_DARK,
  REGISTER_BG_LIGHT,
  REGISTER_DARK,
  REGISTER_LIGHT,
  RegisterField,
  registerStyles,
  registerType,
  StarIcon,
  WIDE_LAYOUT_MIN_WIDTH,
} from './RegisterScreen';

// A passkey notice is a hint, not a form error: it clears itself after a few
// seconds so the person can just continue with email + password (owner,
// 09.10.2026). Password errors keep their banner — the support code and the
// «resend confirmation» link need time to be read.
const PASSKEY_NOTICE_MS = 3000;

type LoginScreenProps = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Login'>;
};

/** Open redirect protection: only allow internal paths */
const isValidReturnTo = (path?: string): path is string =>
  !!path && path.startsWith('/') && !path.startsWith('//') && !path.includes('://');

function navigateInternalReturnTo(
  path: string,
  navigation: NativeStackNavigationProp<RootStackParamList, 'Login'>,
): boolean {
  const [pathWithQuery, hash = ''] = path.split('#');
  const [pathname, queryString = ''] = pathWithQuery.split('?');
  const query = new URLSearchParams(queryString);
  const initialTab = query.get('initialTab') || query.get('tab') || (hash === 'lightPath' ? 'lightPath' : undefined);
  const reset = (name: keyof RootStackParamList, params?: object) => {
    navigation.reset({
      index: 0,
      routes: [{ name, params } as any],
    });
  };

  if (pathname === '/' || pathname === '') {
    reset('Home');
    return true;
  }

  if (pathname === '/dashboard' || pathname === '/me') {
    // CORE-1266 — a «Мои Live-сессии» section (`#rooms/…`) lives ONLY in the
    // fragment, and an internal reset drops it (React Navigation owns the URL
    // and rewrites it without the hash). Fall through to the full-reload branch
    // below so the section the master asked for survives login and the alias
    // layer re-resolves it.
    if (hash.startsWith('rooms/')) return false;
    // CORE-1696 — the same for a master's discussion (`?viewedUserId=…
    // #communityChat/<id>`): the reset would drop both the viewed master and
    // the discussion, landing the guest on their own dashboard instead.
    if (hash.startsWith('communityChat/')) return false;
    // CORE-1696 (QA 17.10) — a guest sent to sign in from ANOTHER master's
    // profile (`?viewedUserId=…`, whatever the tab) must come back to that
    // master: the reset would open their own dashboard instead.
    if (query.has('viewedUserId')) return false;
    reset('Dashboard', initialTab ? { initialTab } : undefined);
    return true;
  }

  // CORE-1198: keep the post-login room return inside the navigator.
  // Reloading the document fires the active session's beforeunload guard.
  //
  // CORE-1341 — обработчик у этого пути ОДИН, и это половина сути правки.
  // Ниже жила вторая ветка (CORE-1190) ровно для
  // `/practice-rooms/session/<код>`, и она делала правильный `reset`. Только
  // до неё управление не доходило: этот regex ловит тот же путь раньше и
  // делал `replace`. Побеждал порядок, а не замысел, — поэтому дубль убран,
  // а его довод (возврат переходом, без перезагрузки: она поднимает
  // `beforeunload` живой сессии и системное окно «Закрыть сайт?», возврат QA
  // 02.09.2026) перенесён сюда и продолжает соблюдаться.
  const roomRoute = pathname.match(/^\/(room|live-session\/join|practice-rooms\/session)\/([^/]+)\/?$/);
  if (roomRoute) {
    const roomId = decodeURIComponent(roomRoute[2]);
    const screen = roomRoute[1] === 'room'
      ? 'RoomAnnouncement'
      : roomRoute[1] === 'live-session/join' ? 'LiveSessionEntry' : 'LiveRoomSession';
    // CORE-1341 — не `replace`. Он заменяет ЭКРАН ВХОДА, а комната, из которой
    // гость сюда ушёл, лежит слоем ниже и остаётся смонтированной: после входа
    // комнат в стеке становится две, и два `<video>` душат друг друга на одном
    // файле. Разбор и замер — в `buildRoomReturnRoutes`.
    const routes = buildRoomReturnRoutes(navigation.getState().routes, screen, { roomId });
    // CORE-1959 — экран той же комнаты под входом сейчас размонтируется, но
    // человек из комнаты не уходит: его место занимает свежий экран. Пометка до
    // сброса — очистка хаба старого экрана не шлёт `LeaveParty`.
    markRoomHandoff(handedOffRoomRouteKeys(navigation.getState().routes, screen, { roomId }));
    navigation.reset({ index: routes.length - 1, routes: routes as any });
    return true;
  }

  if (pathname === '/practices') {
    reset('Practices');
    return true;
  }

  // CORE-1299 — страница эфира. Её здесь не было вовсе: `/live/{id}` доходил до
  // запасной ветки, и на вебе возврат шёл полной перезагрузкой, а на телефоне
  // не происходило НИЧЕГО — человек оставался на форме входа, уже войдя.
  // `invite` и `preview` переносим: без них закрытый эфир (CORE-721) откажет
  // ровно тому, кто по приглашению и пришёл.
  const liveDetail = pathname.match(/^\/live\/(\d+)$/);
  if (liveDetail) {
    const invite = query.get('invite');
    const preview = query.get('preview');
    reset('LiveDetail', {
      id: Number(liveDetail[1]),
      ...(invite ? { invite } : {}),
      ...(preview ? { preview } : {}),
    });
    return true;
  }

  if (pathname === '/masters') {
    reset('Masters', { freshAt: Date.now() });
    return true;
  }

  // CORE-1214 — вернуть на покупку той же программы.
  //
  // Без этой ветки путь до сюда не доходил и на вебе отрабатывал запасной
  // `location.href` — то есть полная перезагрузка вместо перехода. На телефоне
  // же не отрабатывало ничто: человек оставался на экране входа, хотя вошёл.
  //
  // Под флагом, потому что в мобильном билде экранов программ нет вовсе
  // (CORE-1157): сброс на незарегистрированный экран — это не возврат, а падение
  // навигации. Там ссылка честнее остаётся необработанной.
  const programs = PROGRAMS_SURFACE_ENABLED
    ? pathname.match(/^\/programs\/(\d+)(\/purchase)?$/)
    : null;
  if (programs) {
    const programId = Number(programs[1]);
    reset(programs[2] ? 'ProgramPurchase' : 'ProgramPage', { programId });
    return true;
  }

  if (pathname === '/lives' || pathname === '/schedule') {
    const eventId = query.get('eventId');
    reset('Lives', { eventId: eventId ? Number(eventId) : undefined });
    return true;
  }

  return false;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ navigation }) => {
  const { t } = useI18n('auth');
  const route = useRoute<RouteProp<RootStackParamList, 'Login'>>();
  const returnTo = route.params?.returnTo;
  // CSS-mask the password field on Safari/WebKit. Safari ignores
  // `autoComplete="off"` on `<input type="password">` and the iCloud
  // Keychain popup it spawns blocks input on macOS (focus hijack)
  // and the on-screen keyboard on iOS. Rendering as `type="text"` +
  // a masking font looks identical to the user but hides the field
  // from Keychain. See `isSafariOnWeb` for the full rationale.
  const safariMask = isSafariOnWeb();
  const { login, loginWithPasskey, isAuthenticated, isLoading: authLoading } = useAuth();
  const { isDark } = useTheme();
  const palette = isDark ? REGISTER_DARK : REGISTER_LIGHT;
  const { width } = useWindowDimensions();
  const wide = width >= WIDE_LAYOUT_MIN_WIDTH;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const passkeyNoticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (passkeyNoticeTimer.current) clearTimeout(passkeyNoticeTimer.current); }, []);
  const showPasskeyNotice = (message: string) => {
    if (passkeyNoticeTimer.current) clearTimeout(passkeyNoticeTimer.current);
    setError(message);
    passkeyNoticeTimer.current = setTimeout(() => {
      passkeyNoticeTimer.current = null;
      // Only clear our own notice — a password error shown meanwhile stays.
      setError((current) => (current === message ? '' : current));
    }, PASSKEY_NOTICE_MS);
  };
  // CORE-1200 — код показываем только там, где он что-то значит: при сбое,
  // который чинится не пользователем. Под «неверный пароль» он был бы шумом.
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorCodeCopied, setErrorCodeCopied] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  // CORE-576 — set when the BE answers EMAIL_NOT_CONFIRMED, so we can offer
  // «отправить письмо повторно» right in the error banner (as on register).
  const [emailUnconfirmed, setEmailUnconfirmed] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { analytics.track('login_page_viewed', { source: returnTo ? 'redirect' : 'direct' }); }, []);

  // Probe for WebAuthn support so we only show «Войти через passkey» on
  // browsers that can actually handle it. Older Safaris, IE etc. fall back
  // to email + password.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    (async () => {
      try {
        const { isPasskeySupported } = await import('../services/webauthn');
        setPasskeySupported(isPasskeySupported());
      } catch { /* dynamic import failed — leave button hidden */ }
    })();
  }, []);

  /** Reset the banner and the unconfirmed-email affordance together. */
  const clearError = () => {
    setError('');
    setEmailUnconfirmed(false);
    setResendSent(false);
  };

  const handlePasskeyLogin = async () => {
    if (isPasskeyLoading) return;
    setIsPasskeyLoading(true);
    clearError();
    analytics.track('login_passkey_submitted');
    try {
      // Pass the email if the user has typed one — narrows the allow-list
      // server-side and lets the browser pick the right credential
      // automatically. Empty email = discoverable flow (the platform shows
      // a chooser of all stored passkeys for this site).
      await loginWithPasskey(email || undefined);
      analytics.track('login_passkey_success');
      // Redirect handled by the isAuthenticated useEffect below — keeps the
      // returnTo logic in one place across email+password and passkey paths.
    } catch (err: any) {
      // The browser raises NotAllowedError when the user dismisses the
      // platform UI — that's a cancel, not a failure worth alarming about.
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        analytics.track('login_passkey_cancelled');
      } else if (err?.status === 404) {
        // BE missing the passkey endpoints (yet to deploy or feature off).
        // Hide the button on this render so the user doesn't keep clicking,
        // and surface a soft notice rather than the raw "404".
        analytics.track('login_passkey_unavailable');
        setPasskeySupported(false);
        showPasskeyNotice(t('login.passkeyUnavailable'));
      } else {
        // Raw DOMException / API text goes to analytics only; the banner gets
        // calm copy keyed off the failure kind (owner, 09.10.2026).
        analytics.track('login_passkey_error', { error_type: err?.name || err?.message || 'unknown' });
        showPasskeyNotice(t(passkeyErrorKey(err, 'login')));
      }
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  // UC-08.01 AC-05: redirect authenticated users away from /login.
  // Single source of truth for post-auth redirect — also covers the
  // post-handleSubmit / handlePasskeyLogin flip (the auth handlers no longer
  // redirect directly to avoid racing with this effect and falling back to
  // Home when the returnTo branch had already won).
  // CORE-513 — уже авторизованный на `/login` уводится общим правилом
  // (`navigation/authRoutes`); вошедший здесь идёт по ветке returnTo ниже.
  const arrivedAuthenticated = useAuthScreenRedirect({
    navigation, isAuthenticated, isLoading: authLoading,
  });

  // CORE-1299 — эфир, из которого человек пришёл на вход, переживает не только
  // этот экран. Параметр маршрута умирает на подтверждении почты: ссылка из
  // письма открывает новый документ, и назначения там нет. Запись в хранилище
  // делается здесь же, потому что это единственное место, через которое
  // проходят ВСЕ входы в форму, откуда бы её ни открыли.
  //
  // Открытая без эфира форма назначение СТИРАЕТ, и это не побочный эффект:
  // человек, зашедший на вход с главной, выбрал контекст так же явно, как
  // пришедший из комнаты, и вчерашнее приглашение не вправе его увести.
  useEffect(() => {
    void rememberLiveReturn(returnTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    // CORE-1299 — назначение своё отработало: дальше им распоряжается уже
    // ветка ниже. Оставленное, оно увело бы в тот же эфир следующий, ничем с
    // ним не связанный вход с этого устройства.
    void forgetLiveReturn();
    let current = true;
    // Allow the remounted navigator to attach its URL-sync listeners first.
    const timer = setTimeout(() => {
      if (isValidReturnTo(returnTo)
        && navigateInternalReturnTo(returnTo, resetAfterPreload(navigation, () => current))) {
        return;
      }
      if (isValidReturnTo(returnTo) && Platform.OS === 'web') {
        (window as any).location.href = returnTo;
        return;
      }
      // CORE-513 (QA FAIL 27.08) — «назад» уместно только для вошедшего ЗДЕСЬ:
      // он пришёл с какой-то страницы, туда и возвращается. Пришедшего уже
      // авторизованным возвращать некуда — в свежей вкладке позади пусто, и
      // `goBack()` молчит. Его уводит общий `useAuthScreenRedirect` ниже.
      if (arrivedAuthenticated) return;
      if (navigation.canGoBack()) {
        navigation.goBack();
        return;
      }
      navigation.navigate('Home');
    }, 0);
    return () => { current = false; clearTimeout(timer); };
  }, [isAuthenticated, navigation, returnTo, arrivedAuthenticated]);

  const validateEmail = (value: string): string | undefined => {
    if (!value) return t('validation.emailRequired');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return t('validation.emailInvalid');
    return undefined;
  };

  const validatePassword = (value: string): string | undefined => {
    if (!value) return t('validation.passwordRequired');
    return undefined;
  };

  const handleSubmit = async () => {
    if (isLoading) return;

    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    setFieldErrors({ email: emailError, password: passwordError });

    if (emailError || passwordError) return;

    setIsLoading(true);
    clearError();
    setErrorCode(null);
    setErrorCodeCopied(false);

    analytics.track('login_submitted');
    try {
      await login({ email, password }, { returnTo });
      analytics.track('login_success');
      // Redirect is handled by the isAuthenticated useEffect above —
      // having two branches racing was causing the returnTo path to lose
      // to the fallback Home navigation on certain stacks.
    } catch (err) {
      const apiError = err as ApiError;
      // CORE-576 — key off the BE error code and render localized copy. The raw
      // `message` is English and used to leak straight into the banner; keep it
      // only as a last resort for responses that carry no known code.
      const codeKey = authErrorKey(apiError);
      analytics.track('login_error', {
        error_type: apiError.message || 'unknown',
        code: apiError.code,
      });
      setError(codeKey ? t(codeKey) : (apiError.message || t('validation.loginFailed')));
      // CORE-1200 — правило «показывать ли код» общее с регистрацией и живёт
      // в `resolveAuthErrorCode`: две инлайновые копии уже разошлись однажды.
      setErrorCode(resolveAuthErrorCode(apiError));
      setEmailUnconfirmed(apiError.code === 'EMAIL_NOT_CONFIRMED');
      setResendSent(false);
    } finally {
      setIsLoading(false);
    }
  };

  // popTo, not navigate: in React Navigation 7 navigate('Home') pushes a copy
  // of Home on top of this transparent modal, and the auth screens beneath
  // keep rendering through it (owner's bug report, 09.10.2026).
  const goHome = () => {
    navigation.popTo('Home');
  };

  const goRegister = () => {
    navigation.replace('Register', { returnTo });
  };

  const passwordMaskStyle = safariMask && !showPassword
    ? ({ fontFamily: 'text-security-disc', letterSpacing: 2 } as TextStyle)
    : undefined;

  // CORE-1200 — копирование кода. Веб: clipboard API, если он есть (в
  // небезопасном контексте его нет вовсе); натив — expo-clipboard лениво, чтобы
  // не тянуть модуль в веб-бандл. Текст и так `selectable`, так что провал
  // копирования не оставляет человека без кода.
  const copyErrorCode = async (code: string) => {
    if (await copyAuthErrorCode(code)) setErrorCodeCopied(true);
  };

  const renderErrorBanner = () => {
    if (!error) return null;

    return (
      <View
        style={[
          registerStyles.errorBanner,
          {
            backgroundColor: palette.errorBg,
            borderColor: palette.errorBorder,
          },
          Platform.OS === 'web' && { boxShadow: palette.errorGlow } as any,
        ]}
        accessibilityLiveRegion="polite"
      >
        <Ionicons name="alert-circle-outline" size={18} color={palette.errorText} />
        <View style={registerStyles.errorTextBlock}>
          <Text style={[registerType.errorBanner, { color: palette.errorText }]}>{error}</Text>
          {/* CORE-576 — unconfirmed email is a dead end without this: the user
              can't log in and has no way back to the confirmation letter. */}
          {emailUnconfirmed && !resendSent ? (
            <TouchableOpacity
              onPress={async () => {
                setResendLoading(true);
                try {
                  await api.resendConfirmation(email);
                  setResendSent(true);
                } catch { /* ignore — BE answers 200 regardless */ }
                setResendLoading(false);
              }}
              disabled={resendLoading}
              style={registerStyles.resendLink}
              accessibilityRole="button"
            >
              <Text style={[registerType.link, { color: palette.errorText }]}>
                {resendLoading ? t('register.resending') : t('register.resendConfirmation')}
              </Text>
            </TouchableOpacity>
          ) : null}
          {emailUnconfirmed && resendSent ? (
            <Text style={[registerType.caption, registerStyles.resendSuccess]}>{t('register.resendSuccess')}</Text>
          ) : null}
          {/* CORE-1200 — короткий код попытки. По нему поддержка находит и
              клиентское событие, и серверную запись; отсутствие второй значит,
              что запрос до бэка не дошёл. */}
          <AuthErrorCode
            code={errorCode}
            copied={errorCodeCopied}
            onCopy={(code) => { void copyErrorCode(code); }}
            testIDPrefix="login"
            containerStyle={styles.errorCodeBlock}
            textStyle={[registerType.caption, { color: palette.errorText }]}
            hintStyle={[registerType.caption, { color: palette.errorText, opacity: 0.8 }]}
            linkStyle={[registerType.link, { color: palette.errorText }]}
            linkContainerStyle={registerStyles.resendLink}
          />
        </View>
      </View>
    );
  };

  return (
    <ImageBackground
      source={isDark ? REGISTER_BG_DARK : REGISTER_BG_LIGHT}
      resizeMode="cover"
      style={[registerStyles.background, { backgroundColor: palette.background }]}
      imageStyle={registerStyles.backgroundImage}
    >
      <KeyboardAvoidingView
        style={registerStyles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={registerStyles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          horizontal={false}
        >
          <TouchableOpacity
            style={[
              registerStyles.backButton,
              wide ? registerStyles.backButtonWide : registerStyles.backButtonNarrow,
            ]}
            onPress={goHome}
            activeOpacity={0.72}
            accessibilityRole="button"
            accessibilityLabel={t('login.backHome')}
          >
            <Ionicons name="arrow-back-outline" size={30} color={palette.text} />
            <Text style={[registerType.navigation, { color: palette.text }]}>{t('login.backHome')}</Text>
          </TouchableOpacity>

          <View style={[registerStyles.pageFrame, wide ? registerStyles.pageFrameWide : registerStyles.pageFrameNarrow]}>
            <View style={[wide ? registerStyles.leftColumnWide : registerStyles.leftColumnNarrow]}>
              <BrandLogo isDark={isDark} wide={wide} />
              <Text
                accessibilityRole="header"
                style={[
                  wide ? registerType.displayH1 : registerType.displayH2,
                  registerStyles.heroTitle,
                  !wide && styles.heroTitleNarrow,
                  { color: palette.text },
                ]}
              >
                {t('login.heroTitle')}
              </Text>
              <Text style={[registerType.bodyLarge, registerStyles.heroBody, { color: palette.muted }]}>
                {t('login.heroText')}
              </Text>
              <View
                style={[
                  registerStyles.statusBadge,
                  {
                    backgroundColor: palette.statusBg,
                    borderColor: palette.statusBorder,
                  },
                ]}
              >
                <StarIcon size={25} color={palette.accent} />
                <Text style={[registerType.body, registerStyles.statusBadgeText, { color: palette.text }]}>
                  {t('login.statusPrefix')}
                  <Text style={[registerType.bodyAccent, { color: palette.accentText }]}>{t('login.statusName')}</Text>
                </Text>
              </View>
            </View>

            <View
              style={[
                registerStyles.card,
                wide ? registerStyles.cardWide : registerStyles.cardNarrow,
                styles.loginCard,
                {
                  backgroundColor: palette.cardBg,
                  borderColor: palette.cardBorder,
                },
                Platform.OS === 'web' && { boxShadow: palette.cardShadow, backdropFilter: 'blur(24px)' } as any,
              ]}
            >
              <View style={registerStyles.formHeader}>
                {/* The whole «С возвращением» heading is accent-colored —
                    including the first letter (explicit CORE-668 AC). */}
                <Text style={[registerType.uiH1, !wide && { fontSize: narrowFormTitleSize(width, FORM_TITLE_PX_PER_FONT_PX) }, { color: palette.accentText }]}>{t('login.formTitle')}</Text>
                <Text style={[registerType.body, registerStyles.formIntro, { color: palette.muted }]}>
                  {t('login.formText')}
                </Text>
              </View>

              <WebForm onSubmit={handleSubmit}>
                <View style={registerStyles.form}>
                  {renderErrorBanner()}

                  <RegisterField
                    testID="login-email-input"
                    autoFocus
                    palette={palette}
                    label={t('login.emailLabel')}
                    value={email}
                    onChangeText={(v: string) => {
                      setEmail(v);
                      setFieldErrors(prev => ({ ...prev, email: undefined }));
                      clearError();
                    }}
                    placeholder={t('login.emailPlaceholder')}
                    icon="mail-outline"
                    error={fieldErrors.email}
                    autoCapitalize="none"
                    // Same Safari trap as the password field below: with
                    // autocorrect/spellcheck ON (Safari's default) WebKit fires
                    // correction/composition events that fight RN-Web's controlled
                    // value, reverting keystrokes so the field feels frozen on macOS
                    // — especially once Keychain has autofilled it. An email is never
                    // something to autocorrect or spellcheck, so disabling both is
                    // safe on every browser. (CORE-110 fixed this on password only.)
                    autoCorrect={false}
                    spellCheck={false}
                    // CORE-110 round 4 — the real blocker for Safari login (diagnosed
                    // live via console): iCloud Keychain recognises the email+password
                    // pair as a saved login, auto-fills it pending Touch ID, and LOCKS
                    // both fields so manual keystrokes are silently dropped (reproduces
                    // in Private mode; dismissing the popup doesn't release it). A plain
                    // `type=text` field with no autofill hints types fine even inside the
                    // same form. So on Safari we de-credentialise the email field —
                    // `type=text` (no email-address keyboardType) + `autoComplete=off` +
                    // no contentType — so Safari can't identify a username field and
                    // won't engage the locking saved-login autofill. Trade-off: no
                    // email autofill on Safari login, but the user can type. Other
                    // browsers keep the proper email semantics.
                    keyboardType={safariMask ? 'default' : 'email-address'}
                    editable={!isLoading}
                    autoComplete={safariMask ? 'off' : 'email'}
                    textContentType={safariMask ? 'none' : 'emailAddress'}
                  />

                  <RegisterField
                    testID="login-password-input"
                    palette={palette}
                    label={t('login.passwordLabel')}
                    value={password}
                    onChangeText={(v: string) => {
                      setPassword(v);
                      setFieldErrors(prev => ({ ...prev, password: undefined }));
                      clearError();
                    }}
                    placeholder={t('login.passwordPlaceholder')}
                    icon="lock-closed-outline"
                    rightIcon={showPassword ? 'eye-off-outline' : 'eye-outline'}
                    onRightIconPress={() => setShowPassword(!showPassword)}
                    // Safari ignores `autoComplete="off"` on type=password and
                    // hijacks input via iCloud Keychain, so on Safari/WebKit the
                    // field renders as `type="text"` masked by the
                    // `text-security-disc` FONT (see global.css @font-face) —
                    // invisible to Keychain's password-field detection, unlike
                    // `-webkit-text-security`. Full history: CORE-110 round 4.
                    // Trade-off (owner-accepted 2026-06-05): no iCloud Keychain
                    // tap-to-fill on Safari here, but the user can type.
                    secureTextEntry={safariMask ? false : !showPassword}
                    // When the field falls back to type=text on Safari the browser
                    // re-enables autocorrect/autocapitalize/spellcheck — destructive
                    // for a password (Safari «corrects» keystrokes). Off everywhere;
                    // a password should never be corrected or spell-checked anyway.
                    autoCorrect={false}
                    autoCapitalize="none"
                    spellCheck={false}
                    error={fieldErrors.password}
                    editable={!isLoading}
                    autoComplete={safariMask ? 'off' : 'current-password'}
                    textContentType="password"
                    inputStyle={passwordMaskStyle}
                  />

                  <TouchableOpacity
                    testID="login-forgot-password"
                    style={styles.forgotPasswordLink}
                    onPress={() => navigation.navigate('ForgotPassword')}
                    activeOpacity={0.72}
                    accessibilityRole="button"
                  >
                    <Text style={[registerType.link, { color: palette.accentText }]}>
                      {t('login.forgotPassword')}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    testID="login-submit-button"
                    onPress={handleSubmit}
                    disabled={isLoading}
                    activeOpacity={0.88}
                    style={[
                      registerStyles.primaryButton,
                      { backgroundColor: palette.accent },
                      isLoading && registerStyles.disabled,
                    ]}
                    accessibilityRole="button"
                  >
                    {isLoading ? (
                      <ActivityIndicator color={palette.textOnAccent} />
                    ) : (
                      <Text style={[registerType.buttonPrimary, { color: palette.textOnAccent }]}>
                        {t('login.submit')}
                      </Text>
                    )}
                  </TouchableOpacity>

                  {passkeySupported ? (
                    <View style={styles.passkeyRow}>
                      <TouchableOpacity
                        testID="login-passkey"
                        onPress={handlePasskeyLogin}
                        disabled={isPasskeyLoading}
                        style={[styles.passkeyButton, isPasskeyLoading && registerStyles.disabled]}
                        activeOpacity={0.72}
                        accessibilityRole="button"
                      >
                        <Ionicons name="finger-print-outline" size={18} color={palette.text} />
                        <Text style={[registerType.link, styles.passkeyText, { color: palette.text }]}>
                          {t('login.passkey')}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  ) : null}

                  {/* CORE-108 — Continue with Google / Apple (web-only for now). */}
                  <SocialAuthButtons
                    onError={setError}
                    appearance="register"
                    onSuccess={({ isNewUser }) => {
                      // CORE-513 — через Google/Apple со страницы входа может
                      // прийти человек, которого на платформе ещё нет: для него
                      // это регистрация, и приветствие Искателя он должен
                      // увидеть так же, как пришедший почтой. Существующего не
                      // трогаем — им занимается обычный флоу входа.
                      if (isNewUser) navigation.replace('Register', { welcome: true });
                    }}
                  />

                  <View style={registerStyles.footerRow}>
                    <Text style={[registerType.body, { color: palette.muted }]}>{t('login.newToAgape')}</Text>
                    <TouchableOpacity onPress={goRegister} activeOpacity={0.72} accessibilityRole="button">
                      <Text style={[registerType.link, registerStyles.footerLink, { color: palette.accentText }]}>
                        {t('login.createAccount')}
                      </Text>
                    </TouchableOpacity>
                  </View>

                  {/* CORE-840 — условия использования предъявляются до входа. */}
                  <LegalConsentNotice
                    onOpenDoc={(docId) => navigation.navigate('LegalDoc', { docId })}
                    textColor={palette.muted}
                    linkColor={palette.accentText}
                    fontSize={13}
                    align="center"
                  />
                </View>
              </WebForm>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </ImageBackground>
  );
};

// «С возвращением» in Nunito Sans 700 needs ≈8.24 px of width per 1 px of
// font size (measured 09.10.2026) — see narrowTitleSize.ts.
const FORM_TITLE_PX_PER_FONT_PX = 8.24;

const styles = StyleSheet.create({
  errorCodeBlock: { marginTop: 8, gap: 2 },
  // The login card carries less content than the register card, so the
  // register minHeight would leave a hollow bottom — same width, radius and
  // paddings, natural height.
  loginCard: {
    minHeight: 'auto',
  } as any,
  // Mobile web only: the hero heading sits 3px below the displayH2 token
  // (owner's request, 09.10.2026); the wide layout keeps displayH1.
  heroTitleNarrow: {
    fontSize: 37,
  },
  forgotPasswordLink: {
    alignSelf: 'flex-end',
    marginTop: -6,
    marginBottom: 24,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  } as any,
  passkeyRow: {
    marginTop: 16,
    alignItems: 'center',
  },
  passkeyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 18,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  } as any,
  passkeyText: {
    fontSize: 15,
    lineHeight: 22,
  } as any,
});
